import React, { useEffect, useState } from 'react';
import { Button } from 'antd';
import type { ProgramReview } from '../../../interfaces/CamProgramming.interface';

/** `12:04`, or `1:12:04` once an hour has passed. */
function formatElapsed(totalSeconds: number): string {
	const seconds = totalSeconds % 60;
	const minutes = Math.floor(totalSeconds / 60) % 60;
	const hours = Math.floor(totalSeconds / 3600);
	const padded = [minutes, seconds].map((part) => String(part).padStart(2, '0'));
	return hours ? [hours, ...padded].join(':') : padded.join(':');
}

/**
 * Seconds since `startedAt`, ticking while the component is mounted.
 *
 * Measured against the browser's clock, so a machine that disagrees with the
 * server shows a correspondingly skewed figure. Only the display is affected —
 * the duration that gets recorded is measured server-side when the review ends.
 */
function useElapsedSeconds(startedAt: string | null): number {
	const [now, setNow] = useState(() => Date.now());

	useEffect(() => {
		if (!startedAt) return;
		const timer = setInterval(() => setNow(Date.now()), 1000);
		return () => clearInterval(timer);
	}, [startedAt]);

	if (!startedAt) return 0;
	return Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 1000));
}

interface ReviewClockProps {
	review: ProgramReview;
	onEnd: () => void;
	isEnding: boolean;
}

/**
 * How long the review has been running, and the way to stop it. A finished
 * review shows what it took instead.
 */
const ReviewClock: React.FC<ReviewClockProps> = ({
	review,
	onEnd,
	isEnding,
}) => {
	// Reviews logged by hand never recorded a start, so the row's own age is
	// the closest thing to one.
	const startedAt = review.review_started_at ?? review.created_time;
	const elapsed = useElapsedSeconds(
		review.review_completed_at ? null : startedAt
	);

	if (review.review_completed_at) {
		const minutes = review.review_duration_minutes;
		return (
			<div className="embed-clock embed-clock--done">
				<span className="embed-clock__label">
					Review ended{minutes ? ` after ${minutes} min` : ''}
				</span>
			</div>
		);
	}

	return (
		<div className="embed-clock">
			<span className="embed-clock__time" role="timer" aria-live="off">
				{formatElapsed(elapsed)}
			</span>
			<Button size="small" danger onClick={onEnd} loading={isEnding}>
				End Review
			</Button>
		</div>
	);
};

export default ReviewClock;
